/**
 * What the roadmap does not yet say.
 *
 * This is the panel the workbook could not have had. Excel rendered a missing date,
 * a #REF! and a stray zero identically - as an empty cell or as a bar in January
 * 1900 - so the sheet could be wrong in ways nobody could see by looking at it. The
 * same analysis the migration ran once now runs on every request against live data,
 * and this puts the answer next to the chart rather than in a report somebody has to
 * remember to open.
 *
 * The count is currently large and that is correct, not a bug to hide: every owner
 * field came across the migration null because the name-per-project mapping has
 * never existed, and they were deliberately not guessed. Clicking a row opens the
 * lane it belongs to so the gap can be filled in place.
 */

import styled from 'styled-components';

import { palette, radius } from '../styles/theme';
import { Chip, Panel } from '../styles/ui';
import type { Gap, GapReport } from '../types';

const Head = styled.header`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const Title = styled.h2`
  font-size: 15px;
`;

const Meter = styled.div`
  height: 8px;
  border-radius: ${radius.pill};
  background: ${palette.blush};
  border: 1px solid ${palette.border};
  overflow: hidden;
  margin-bottom: 12px;
`;

const MeterFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: ${palette.hotPink};
`;

/*
  Sized against the viewport rather than a fixed 320px. There are 38 gaps against
  the live data and a flat 320px showed nine of them beside a chart four times that
  tall - the list looked truncated rather than scrollable, because macOS overlay
  scrollbars are invisible until you actually scroll.

  The subtrahend is the masthead, toolbar and the panel's own heading and meter. The
  floor matters more than the arithmetic: below it the panel is short enough that
  dropping it under the chart (the 1180px breakpoint in App.tsx) reads better anyway.
*/
const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: max(320px, calc(100vh - 260px));
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Entry = styled.li`
  display: flex;
`;

const EntryButton = styled.button`
  flex: 1;
  display: flex;
  align-items: baseline;
  gap: 8px;
  text-align: left;
  font-family: inherit;
  font-size: 12.5px;
  color: ${palette.ink};
  background: transparent;
  border: 1px solid transparent;
  border-radius: ${radius.sm};
  padding: 5px 8px;
  cursor: pointer;

  &:hover {
    background: ${palette.blush};
    border-color: ${palette.border};
  }

  &:focus-visible {
    outline: 2px solid ${palette.hotPink};
    outline-offset: 1px;
  }
`;

const Where = styled.span`
  font-weight: 700;
  white-space: nowrap;
`;

const What = styled.span`
  color: ${palette.inkSoft};
`;

const Clean = styled.p`
  margin: 0;
  color: ${palette.inkSoft};
`;

/*
  The milestone tally, kept OUT of the meter above it.

  The meter reads "how much of the schedule is real" and its denominator is phases.
  Folding milestones in would move a number that is quoted across reports, for reasons
  that have nothing to do with anyone filling anything in - so they get their own line
  with their own denominator. The backend draws the same distinction: see the
  MilestoneCounts docstring in fast/app/routes/roadmap.py.
*/
const Tally = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  color: ${palette.inkSoft};
`;

/** A missed deadline is not a blank field. It is the only red thing on this panel. */
const Missed = styled.strong`
  color: ${palette.danger};
`;

export interface GapsPanelProps {
  report: GapReport;
  onSelect: (gap: Gap) => void;
}

export default function GapsPanel({ report, onSelect }: GapsPanelProps) {
  return (
    <Panel aria-labelledby="gaps-title">
      <Head>
        <Title id="gaps-title">Still to decide</Title>
        <Chip>
          {report.fully_populated} of {report.real_phases} phases fully specified ·{' '}
          {report.fully_populated_pct}%
        </Chip>
      </Head>

      <Meter
        role="img"
        aria-label={`${report.fully_populated_pct}% of phases fully specified`}
        title={
          // The denominator excludes the structural Maintenance bands: they carry no
          // dates by design, and counting them made the migration report 14 false
          // entries that buried the ones needing a real answer.
          `${report.real_phases} phases of scheduled work across ${report.total_projects} projects. ` +
          `${report.structural_phases} ongoing-support bands are excluded - they carry no dates by design.`
        }
      >
        <MeterFill $pct={report.fully_populated_pct} />
      </Meter>

      {report.total_milestones > 0 ? (
        <Tally>
          {report.total_milestones} milestone{report.total_milestones === 1 ? '' : 's'}
          {report.undated_milestones > 0
            ? ` · ${report.undated_milestones} with no date set`
            : ''}
          {report.missed_milestones > 0 ? (
            <>
              {' · '}
              <Missed>
                {report.missed_milestones} missed
              </Missed>
            </>
          ) : null}
        </Tally>
      ) : null}

      {report.gaps.length === 0 ? (
        <Clean>Nothing outstanding.</Clean>
      ) : (
        <List>
          {report.gaps.map((gap) => (
            // The milestone id is part of the key, not decoration. A project with two
            // undated milestones produces two gaps with the same project, the same
            // null phase and the same field - so keying on those three alone
            // collides, and React renders one of them and drops the other.
            <Entry
              key={[gap.project_id, gap.phase_id ?? '-', gap.milestone_id ?? '-', gap.field].join(
                '/'
              )}
            >
              <EntryButton type="button" onClick={() => onSelect(gap)}>
                <Where>
                  {gap.project}
                  {gap.phase ? ` · ${gap.phase}` : ''}
                  {gap.milestone ? ` · ${gap.milestone}` : ''}
                </Where>
                <What>
                  {gap.field}: {gap.problem}
                </What>
              </EntryButton>
            </Entry>
          ))}
        </List>
      )}
    </Panel>
  );
}
